import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container, IconButton } from "@mui/material";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";

const cardImages = [
  "/static/images/card-brain-1.webp",
  "/static/images/card-brain-3.png",
  "/static/images/card-brain-2.webp",
];

/* Soft pastel card backgrounds — richer gradients */
const cardBgs = [
  "linear-gradient(145deg, #EDE4F8 0%, #D6CCE8 50%, #C8BEE0 100%)",
  "linear-gradient(145deg, #F2E4F0 0%, #E8D0E4 50%, #DFC8DE 100%)",
  "linear-gradient(145deg, #FCE8EC 0%, #F8D4DC 50%, #F2CAD5 100%)",
];

/* Accent colors for tags & hovers */
const cardAccents = ["#7C3AED", "#A855F7", "#EC4899"];

/* Glow colors for hover effect */
const cardGlows = [
  "rgba(124,58,237,0.12)",
  "rgba(168,85,247,0.12)",
  "rgba(236,72,153,0.12)",
];

/* Card heights — ladder effect */
const cardHeights = [
  "md:min-h-[380px]",
  "md:min-h-[460px]",
  "md:min-h-[540px]",
];

const ShowcaseSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const badge = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.showcase.badge",
  });

  const items = [0, 1, 2].map((i) => ({
    tag: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.showcase.items.${i}.tag`,
    }),
    title: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.showcase.items.${i}.title`,
    }),
    image: cardImages[i],
    bg: cardBgs[i],
    accent: cardAccents[i],
    glow: cardGlows[i],
    height: cardHeights[i],
  }));

  return (
    <Box
      className="py-24 relative overflow-hidden"
      sx={{ background: "transparent" }}
    >
      {/* Atmospheric glow blobs */}
      <motion.div
        className="absolute top-[5%] left-[-5%] w-[35%] h-[40%] rounded-full bg-[#EDE4F8]/60 blur-[130px] pointer-events-none"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[5%] right-[-5%] w-[30%] h-[35%] rounded-full bg-[#F5E1EC]/50 blur-[110px] pointer-events-none"
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />
      <motion.div
        className="absolute top-[40%] left-[30%] w-[25%] h-[30%] rounded-full bg-[#D4C5F9]/30 blur-[100px] pointer-events-none"
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 5 }}
      />

      <Container maxWidth="xl" className="relative z-10" dir={dir}>
        {/* Badge + horizontal line */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-4 mb-16 px-4 md:px-8"
        >
          <div className="flex items-center gap-2">
            <motion.svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <path
                d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z"
                fill="#8B5CF6"
                fillOpacity="0.5"
              />
            </motion.svg>
            <Typography
              className="text-sm font-semibold"
              sx={{ color: "#7C3AED" }}
            >
              {localizationProvider.translateByType({
                type: "Localized",
                text: "landing.nav.brand",
              })}
            </Typography>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-[#8B5CF6]/25 to-transparent" />
          <Typography className="text-sm" sx={{ color: "#8B7BA0" }}>
            {badge}
          </Typography>
        </motion.div>

        {/* Three asymmetric cards — bottom-aligned ladder */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4 md:px-8 md:items-end">
          {items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
            >
              <div
                className={`group relative rounded-[2rem] ${item.height} p-6 overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-3 flex flex-col`}
                style={{
                  background: item.bg,
                  border: "1px solid rgba(255,255,255,0.7)",
                  boxShadow: `0 4px 24px rgba(139,92,246,0.06), 0 0 0 1px rgba(255,255,255,0.3) inset`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 20px 60px ${item.glow}, 0 0 0 1px rgba(255,255,255,0.5) inset`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 4px 24px rgba(139,92,246,0.06), 0 0 0 1px rgba(255,255,255,0.3) inset`;
                }}
              >
                {/* Tag + Arrow header */}
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="inline-block px-3.5 py-1.5 rounded-full text-xs font-medium border z-[2] backdrop-blur-sm"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.6)",
                      color: item.accent,
                      borderColor: `${item.accent}20`,
                    }}
                  >
                    {item.tag}
                  </span>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.5)",
                      border: `1px solid ${item.accent}15`,
                      width: 36,
                      height: 36,
                      color: item.accent,
                      backdropFilter: "blur(8px)",
                      "&:hover": {
                        bgcolor: item.accent,
                        color: "white",
                        boxShadow: `0 6px 24px ${item.accent}35`,
                      },
                      transition: "all 0.3s",
                    }}
                  >
                    <NorthEastRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </div>

                {/* Title */}
                <Typography
                  className="text-xl md:text-2xl font-bold tracking-tight leading-snug mb-4 max-w-[200px]"
                  sx={{ zIndex: 2, color: "#2D1B4E" }}
                >
                  {item.title}
                </Typography>

                <div className="flex-1" />

                {/* Image — bottom-end corner */}
                <div className="relative flex justify-end mt-auto">
                  <Box
                    className="w-[85%] overflow-hidden rounded-tl-2xl"
                    sx={{
                      position: "absolute",
                      left: -140,
                      bottom: `-${(items.length - i) * 60}px`,
                      minWidth: { xs: "150%", md: "110%" },
                      minHeight: { xs: "150%", md: "110%" },
                      zIndex: 0,
                      opacity: 0.9,
                    }}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </Box>
                </div>

                {/* Animated decorative sparkle */}
                <motion.svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="absolute top-6 right-[35%] pointer-events-none"
                  animate={{
                    opacity: [0.15, 0.5, 0.15],
                    scale: [0.8, 1.3, 0.8],
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                >
                  <path
                    d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z"
                    fill={item.accent}
                    fillOpacity="0.4"
                  />
                </motion.svg>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </Box>
  );
};

export default ShowcaseSection;
